import type { PurchaseOrderDetail } from '@/types'
import { printThermalHtmlDocument } from '@/lib/printKotReceipt'
import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import type { CustomerReceiptSettings } from '@/lib/printCustomerReceipt'
import { formatDateHuman, formatDateTimeHuman } from '@/lib/utils'
import { poRef as poRefFromId, poStatusLabel } from '@/components/store/poStatus'
import { poLineQtyText } from '@/components/store/purchasing/purchasingPilotLogic'

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// One order number everywhere: the slip prints the same #REF the queue and
// detail sheet show (poStatus.poRef is the single source).
function poRef(po: PurchaseOrderDetail): string {
  return poRefFromId(po.id)
}

const statusLabel = poStatusLabel

type PurchaseOrderPrintOpts = {
  businessName: string
  formatCurrency: (n: number) => string
  paperWidthMm: number
  printableAreaMm: number
  fontScale: number
}

export function buildPurchaseOrderPrintHtml(
  po: PurchaseOrderDetail,
  opts: PurchaseOrderPrintOpts,
): string {
  const fmt = opts.formatCurrency
  const created = formatDateTimeHuman(po.created_at)
  const expected =
    po.expected_date && po.expected_date.trim()
      ? formatDateHuman(po.expected_date)
      : ''

  let grand = 0
  let anyCost = false
  const rows = po.lines.map((ln, idx) => {
    const name = (ln.item_name || 'Item').toUpperCase()
    // Kitchen flow pilot (Batch 5): "2 bags (50 kg)" when the line has a pack.
    const qtyStr = ln.pack_qty && ln.pack_qty > 0
      ? poLineQtyText(Number(ln.quantity_ordered), ln.unit || '', { label: ln.pack_label || 'pack', qty: ln.pack_qty })
      : `${Number(ln.quantity_ordered)} ${ln.unit || ''}`.trim()
    const uc = ln.unit_cost
    const has = uc != null && Number.isFinite(Number(uc))
    if (has) {
      anyCost = true
      const lineTot = Number(ln.quantity_ordered) * Number(uc)
      grand += lineTot
    }
    const unitCell = has ? fmt(Number(uc)) : '—'
    const lineCell = has ? fmt(Number(ln.quantity_ordered) * Number(uc)) : '—'
    return `<tr>
      <td class="num">${idx + 1}</td>
      <td class="desc">${escapeHtml(name)}</td>
      <td class="num">${escapeHtml(qtyStr)}</td>
      <td class="num">${unitCell}</td>
      <td class="num">${lineCell}</td>
    </tr>`
  })

  const notesBlock =
    po.notes && po.notes.trim()
      ? `<div class="notes"><div class="lbl">Notes</div><div class="notes-body">${escapeHtml(po.notes.trim())}</div></div>`
      : ''

  const totalBlock = anyCost
    ? `<div class="totals"><div class="grand"><span>Est. total</span><span>${fmt(grand)}</span></div></div>`
    : ''

  const baseCss = thermalPrintBaseCss({
    paperWidthMm: opts.paperWidthMm,
    printableAreaMm: opts.printableAreaMm,
    fontScale: opts.fontScale,
  })
  const scale = opts.fontScale
  const w = opts.printableAreaMm

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>PO ${escapeHtml(poRef(po))}</title>
<style>
  ${baseCss}
  body {
    font-family: ui-monospace, 'Cascadia Mono', Consolas, monospace;
    font-size: calc(11px * ${scale});
    font-weight: 700;
    color: #000;
  }
  .receipt { width: ${w}mm; max-width: ${w}mm; margin: 0 auto; box-sizing: border-box; padding: 2mm 2mm 5mm; }
  .center { text-align: center; }
  .brand { font-size: calc(15px * ${scale}); font-weight: 900; letter-spacing: 0.02em; margin-bottom: 2px; }
  .doc-title { font-size: calc(13px * ${scale}); font-weight: 900; margin: 6px 0 2px; letter-spacing: 0.04em; }
  .status-pill { font-size: calc(10px * ${scale}); font-weight: 700; margin-bottom: 6px; }
  .meta { width: 100%; margin: 6px 0; border-collapse: collapse; font-size: calc(10.5px * ${scale}); }
  .meta td { padding: 2px 0; vertical-align: top; }
  .meta .lbl { width: 38%; font-weight: 800; }
  .meta .val { text-align: right; word-break: break-word; padding-right: 1mm; }
  .notes { margin: 8px 0; font-size: calc(10.5px * ${scale}); }
  .notes .lbl { font-weight: 800; margin-bottom: 2px; }
  .notes-body { white-space: pre-wrap; word-break: break-word; line-height: 1.35; }
  hr.sep { border: none; border-top: 1px solid #000; margin: 8px 0; }
  table.items { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: calc(10px * ${scale}); }
  table.items th { border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 3px 1px; text-align: left; font-weight: 900; }
  table.items th:nth-child(n+3), table.items td:nth-child(n+3) { text-align: right; padding-right: 1mm; }
  table.items th.desc, table.items td.desc { text-align: left; max-width: 28mm; word-break: break-word; }
  table.items td { padding: 3px 1px; vertical-align: top; }
  .num { text-align: right; white-space: nowrap; }
  .totals { margin-top: 8px; font-size: calc(12px * ${scale}); }
  .totals .grand { display: flex; justify-content: space-between; font-weight: 900; border-top: 1px dashed #000; padding-top: 6px; margin-top: 4px; }
  .totals .grand span:last-child { padding-right: 1mm; }
  .footer { margin-top: 10px; font-size: calc(9px * ${scale}); text-align: center; color: #333; line-height: 1.4; }
</style></head><body>
  <div class="receipt">
    <div class="center">
      <div class="brand">${escapeHtml(opts.businessName)}</div>
    </div>
    <hr class="sep"/>
    <div class="center doc-title">PURCHASE ORDER</div>
    <div class="center status-pill">${escapeHtml(statusLabel(po.status))}</div>
    <table class="meta">
      <tr><td class="lbl">PO #</td><td class="val">${escapeHtml(poRef(po))}</td></tr>
      <tr><td class="lbl">Supplier</td><td class="val">${escapeHtml(po.supplier_name || '—')}</td></tr>
      <tr><td class="lbl">Created</td><td class="val">${escapeHtml(created)}</td></tr>
      ${expected ? `<tr><td class="lbl">Expected</td><td class="val">${escapeHtml(expected)}</td></tr>` : ''}
    </table>
    ${notesBlock}
    <hr class="sep"/>
    <table class="items">
      <thead><tr><th>#</th><th class="desc">Item</th><th>Qty</th><th>Unit</th><th>Line</th></tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table>
    ${totalBlock}
    <div class="footer">Shopping list / vendor copy — thermal PO slip</div>
  </div>
</body></html>`
}

export function printPurchaseOrder(
  po: PurchaseOrderDetail,
  opts: {
    businessName?: string
    formatCurrency: (n: number) => string
    cfg: Pick<CustomerReceiptSettings, 'paperWidthMm' | 'printableAreaMm' | 'fontScale'>
  },
): void {
  const businessName = (opts.businessName && opts.businessName.trim()) || 'Restaurant'
  const html = buildPurchaseOrderPrintHtml(po, {
    businessName,
    formatCurrency: opts.formatCurrency,
    paperWidthMm: opts.cfg.paperWidthMm,
    printableAreaMm: opts.cfg.printableAreaMm,
    fontScale: opts.cfg.fontScale,
  })
  printThermalHtmlDocument(html, opts.cfg.paperWidthMm)
}
